import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import {
  THEME_STORAGE_KEY, applyTheme, readPref, resolveTheme, themeColorFor, toggleFrom,
  type Theme, type ThemePref,
} from '../lib/theme'

// The per-device appearance preference (ux/03 Theme switch): system by
// default, an explicit light/dark once chosen, kept in localStorage. index.html
// applies the stored value before first paint, so this only keeps it in step.

const DARK = '(prefers-color-scheme: dark)'

function subscribeSystem(onChange: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const mq = window.matchMedia(DARK)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}
const systemDark = () => typeof window !== 'undefined' && !!window.matchMedia?.(DARK).matches

function readStored(): ThemePref {
  try { return readPref(localStorage.getItem(THEME_STORAGE_KEY)) } catch { return 'system' }
}

export function useTheme(): {
  pref: ThemePref
  theme: Theme
  setPref: (pref: ThemePref) => void
  toggle: () => void
} {
  const [pref, setPrefState] = useState<ThemePref>(readStored)
  const sysDark = useSyncExternalStore(subscribeSystem, systemDark, () => false)
  const theme = resolveTheme(pref, sysDark)

  useEffect(() => {
    applyTheme(document.documentElement, pref)
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColorFor(theme))
  }, [pref, theme])

  const setPref = useCallback((next: ThemePref) => {
    // Cross-fade the colours for the switch itself, then drop the class so
    // nothing else ever transitions colour.
    const root = document.documentElement
    root.classList.add('ml-theme-fading')
    window.setTimeout(() => root.classList.remove('ml-theme-fading'), 320)
    try {
      if (next === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
      else localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch { /* private mode: still applies for this session */ }
    setPrefState(next)
  }, [])

  const toggle = useCallback(() => setPref(toggleFrom(pref, sysDark)), [pref, sysDark, setPref])

  return { pref, theme, setPref, toggle }
}
